import { BaseClient } from './BaseClient';
import { Sessions } from '../resources/Sessions';
import { Messages } from '../resources/Messages';
import { Chats } from '../resources/Chats';
import { Contacts } from '../resources/Contacts';
import { Groups } from '../resources/Groups';
import { Status } from '../resources/Status';
import { Webhooks } from '../resources/Webhooks';
import { Admin } from '../resources/Admin';
import { Business } from '../resources/Business';
import { ClientOptions } from '../types';
import { BotFramework, BotConfig } from '../bot';
import { MessageQueue, QueueConfig } from '../queue';
import { MongoDBAdapter, DatabaseConfig } from '../adapters';
import { PollingService, PollingConfig } from '../polling';
import { SessionManager } from '../session';
import { MetricsCollector, MetricsConfig } from '../metrics';

/**
 * Main WhatsApp API Client
 * 
 * @example
 * ```typescript
 * import { WhatsAppClient } from '@chatsuite/sdk';
 * 
 * const client = new WhatsAppClient({
 *   apiToken: 'wa_your_token_here',
 *   baseUrl: 'https://api.chatsuite.com' // optional
 * });
 * 
 * // List sessions
 * const sessions = await client.sessions.list();
 * 
 * // Send a message
 * const message = await client.messages.sendText(
 *   '+1234567890',
 *   '1234567890@c.us',
 *   'Hello from SDK!'
 * );
 * ```
 */
export class WhatsAppClient extends BaseClient {
  /**
   * Session management operations
   */
  public readonly sessions: Sessions;

  /**
   * Message operations
   */
  public readonly messages: Messages;

  /**
   * Chat operations
   */
  public readonly chats: Chats;

  /**
   * Contact operations
   */
  public readonly contacts: Contacts;

  /**
   * Group operations
   */
  public readonly groups: Groups;

  /**
   * Status/Story operations
   */
  public readonly status: Status;

  /**
   * Webhook management operations
   */
  public readonly webhooks: Webhooks;

  /**
   * Admin operations (requires admin permissions)
   */
  public readonly admin: Admin;

  /**
   * Business operations (catalog, orders, cart)
   */
  public readonly business: Business;

  constructor(options: ClientOptions) {
    super(options);

    // Initialize resources
    this.sessions = new Sessions(this);
    this.messages = new Messages(this);
    this.chats = new Chats(this);
    this.contacts = new Contacts(this);
    this.groups = new Groups(this);
    this.status = new Status(this);
    this.webhooks = new Webhooks(this);
    this.admin = new Admin(this);
    this.business = new Business(this);
  }

  /**
   * Health check
   * @returns API health status
   */
  async health(): Promise<{
    status: string;
    environment: string;
    timestamp: string;
  }> {
    return this.get('/health');
  }

  /**
   * Verify API token is valid
   * @returns Token validation result
   */
  async verifyToken(): Promise<boolean> {
    try {
      await this.sessions.list();
      return true;
    } catch (error) {
      if (error instanceof Error && 'status' in error) {
        const apiError = error as any;
        if (apiError.status === 401 || apiError.status === 403) {
          return false;
        }
      }
      throw error;
    }
  }

  /**
   * Create a quick session and wait for it to be ready
   * @param tel - Phone number
   * @param name - Session name
   * @param webhookUrl - Optional webhook URL for receiving events
   * @returns Ready session with webhook setup
   */
  async quickStart(
    tel: string,
    name: string,
    webhookUrl?: string
  ): Promise<{
    session: any;
    webhook?: any;
    sendText: (to: string, text: string) => Promise<any>;
    sendMedia: (to: string, media: string, mimetype: string, filename: string, caption?: string) => Promise<any>;
  }> {
    // Create session
    const session = await this.sessions.create({
      tel,
      name,
      autoRestart: true,
    });

    // Initialize if not already initializing
    if (!session.initializing && !session.ready) {
      await this.sessions.initialize(tel);
    }

    // Wait for QR code
    const sessionWithQR = await this.sessions.pollForQR(tel);
    
    console.log('Scan this QR code with WhatsApp:');
    console.log(sessionWithQR.qr);

    // Wait for connection
    const readySession = await this.sessions.waitForReady(tel);

    // Set up webhook if URL provided
    let webhook;
    if (webhookUrl) {
      webhook = await this.webhooks.setupMessageWebhook(tel, webhookUrl, `${name} Messages`);
      console.log('Webhook configured:', webhook.name);
    }

    // Return convenience methods
    return {
      session: readySession,
      webhook,
      sendText: (to: string, text: string) => 
        this.messages.sendText(tel, to, text),
      sendMedia: (to: string, media: string, mimetype: string, filename: string, caption?: string) =>
        this.messages.sendMedia(tel, to, media, mimetype, filename, caption),
    };
  }

  /**
   * Quick webhook setup for session events
   * @param tel - Phone number
   * @param url - Webhook URL
   * @param events - Events to subscribe to (defaults to message.received)
   * @param name - Optional webhook name
   */
  async setupSessionWebhook(
    tel: string,
    url: string,
    events: string[] = ['message.received'],
    name?: string
  ) {
    return this.webhooks.createForEvents(tel, url, events, {
      name: name || `Webhook for ${tel}`,
    });
  }

  /**
   * Get all webhooks for a session
   * @param tel - Phone number
   */
  async getSessionWebhooks(tel: string) {
    return this.webhooks.list(tel);
  }

  /**
   * Remove all webhooks for a session
   * @param tel - Phone number
   */
  async clearSessionWebhooks(tel: string) {
    const webhooks = await this.webhooks.list(tel);
    const deletePromises = webhooks.map(webhook => 
      this.webhooks.delete(tel, webhook._id)
    );
    return Promise.all(deletePromises);
  }

  /**
   * Upload media file
   * @param tel - Phone number
   * @param media - Base64 encoded media
   * @param mimetype - Media MIME type
   * @param filename - Media filename
   */
  async uploadMedia(
    tel: string,
    media: string,
    mimetype: string,
    filename: string
  ): Promise<{ id: string; url: string }> {
    const response = await this.post<{
      success: boolean;
      media: { id: string; url: string };
    }>('/api/media/upload', {
      tel,
      media,
      mimetype,
      filename,
    });

    return response.media;
  }

  /**
   * Get API usage statistics
   * @param from - Start date (ISO 8601)
   * @param to - End date (ISO 8601)
   */
  async getUsageStats(
    from?: string,
    to?: string
  ): Promise<{
    messages: { sent: number; received: number };
    sessions: { active: number; total: number };
    storage: { used: number; limit: number };
  }> {
    const params = { from, to };
    const response = await this.get<{
      success: boolean;
      stats: any;
    }>('/api/stats', { params });

    return response.stats;
  }

  /**
   * Create a bot framework for automated responses
   * @param tel - Phone number
   * @param config - Bot configuration
   */
  createBot(tel: string, config: BotConfig): BotFramework {
    return new BotFramework(this, tel, config);
  }

  /**
   * Create a message queue for rate-limited sending
   * @param config - Queue configuration
   */
  createMessageQueue(config?: Partial<QueueConfig>): MessageQueue {
    return new MessageQueue(this, config);
  }

  /**
   * Create a database adapter for syncing WhatsApp data
   * @param config - Database configuration
   */
  createDatabaseAdapter(config: DatabaseConfig): MongoDBAdapter {
    return new MongoDBAdapter(config);
  }

  /**
   * Create a polling service for automatic message syncing
   * @param tel - Phone number
   * @param config - Polling configuration
   */
  createPollingService(tel: string, config?: Partial<PollingConfig>): PollingService {
    return new PollingService(this, tel, config);
  }

  /**
   * Create a session manager for advanced session handling
   * @param config - Session manager configuration
   */
  createSessionManager(config?: {
    autoRestart?: boolean;
    maxRestartAttempts?: number;
    restartDelay?: number;
    healthCheckInterval?: number;
  }): SessionManager {
    return new SessionManager(this, undefined, config);
  }

  /**
   * Create a metrics collector for performance monitoring
   * @param config - Metrics configuration
   */
  createMetricsCollector(config: MetricsConfig): MetricsCollector {
    return new MetricsCollector(config);
  }

  /**
   * Complete WhatsApp automation setup with all features
   * @param tel - Phone number
   * @param config - Automation configuration
   */
  async createAutomation(tel: string, config: {
    name: string;
    webhookUrl?: string;
    bot?: BotConfig;
    queue?: Partial<QueueConfig>;
    database?: DatabaseConfig;
    polling?: Partial<PollingConfig>;
    metrics?: MetricsConfig;
  }) {
    // Quick session setup
    const sessionResult = await this.quickStart(tel, config.name, config.webhookUrl);

    // Create components based on config
    const components: any = {
      session: sessionResult.session,
      webhook: sessionResult.webhook,
      sendText: sessionResult.sendText,
      sendMedia: sessionResult.sendMedia
    };

    // Bot framework
    if (config.bot) {
      components.bot = this.createBot(tel, config.bot);
      components.bot.start();
    }

    // Message queue
    if (config.queue) {
      components.queue = this.createMessageQueue(config.queue);
      components.queue.start();
    }

    // Database adapter
    if (config.database) {
      components.database = this.createDatabaseAdapter(config.database);
      await components.database.connect();
    }

    // Polling service
    if (config.polling) {
      components.polling = this.createPollingService(tel, config.polling);
      components.polling.start();
    }

    // Metrics collector
    if (config.metrics) {
      components.metrics = this.createMetricsCollector(config.metrics);
    }

    return components;
  }
}